/**
 * PayPal Integration for Firebase
 * 
 * Handles PayPal checkout flow by calling Cloud Functions.
 * 
 * ENVIRONMENT SETUP:
 * Add to your .env file:
 *   VITE_PAYPAL_CLIENT_ID=your_sandbox_client_id_here
 *   VITE_PAYPAL_MODE=sandbox  (or 'live' for production)
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from './config';

/**
 * Get PayPal Client ID from environment
 * @returns {string} PayPal Client ID
 */
export const getPayPalClientId = () => {
    const clientId = import.meta.env.VITE_PAYPAL_CLIENT_ID;
    if (!clientId || clientId.trim() === '') {
        console.warn('PayPal Client ID not configured. Set VITE_PAYPAL_CLIENT_ID in your .env file.');
        return '';
    }
    return clientId;
};

/**
 * Get PayPal mode (sandbox or live)
 * @returns {string} 'sandbox' or 'live'
 */
export const getPayPalMode = () => {
    const mode = import.meta.env.VITE_PAYPAL_MODE || 'sandbox';
    return mode.toLowerCase() === 'live' ? 'production' : 'sandbox';
};

/**
 * Check if PayPal is configured
 * @returns {boolean}
 */
export const isPayPalConfigured = () => {
    const clientId = getPayPalClientId();
    return clientId && clientId.trim() !== '';
};

/**
 * Get PayPal key status for admin display
 * @returns {{configured: boolean, masked: string, mode: string}}
 */
export const getPayPalKeyStatus = () => {
    const clientId = getPayPalClientId();
    const mode = getPayPalMode();

    if (!clientId || clientId.trim() === '') {
        return {
            configured: false,
            masked: '',
            mode: mode
        };
    }

    // Mask the client ID for display (show first 8 and last 4 chars)
    const masked = clientId.length > 12
        ? `${clientId.substring(0, 8)}...${clientId.slice(-4)}`
        : clientId;

    return {
        configured: true,
        masked: masked,
        mode: mode
    };
};

/**
 * Create a PayPal order via Cloud Function
 * Called when PayPal button is clicked
 * 
 * @param {Array} items - Cart items
 * @param {string} customerEmail - Customer email
 * @param {string} customerName - Customer name (optional)
 * @returns {Promise<{success: boolean, orderId?: string, paypalOrderId?: string, error?: string}>}
 */
export const createPayPalOrder = async (items, customerEmail, customerName = '') => {
    try {
        const createOrder = httpsCallable(functions, 'createPayPalOrder');

        const result = await createOrder({
            items: items.map(item => ({
                productId: item.productId || item.id,
                title: item.title || item.name,
                name: item.title || item.name,
                description: item.description || '',
                price: parseFloat(item.price),
                quantity: item.quantity || 1,
                thumbnail: item.thumbnail || null
            })),
            customerEmail: customerEmail?.toLowerCase().trim(),
            customerName: customerName?.trim() || ''
        });

        if (result.data?.success) {
            return {
                success: true,
                orderId: result.data.orderId,
                paypalOrderId: result.data.paypalOrderId
            };
        } else {
            throw new Error(result.data?.error || 'Failed to create PayPal order');
        }
    } catch (error) {
        console.error('Error creating PayPal order:', error);

        // Handle specific Firebase callable errors
        if (error.code === 'functions/failed-precondition') {
            return {
                success: false,
                error: 'PayPal is not configured. Please contact the administrator.',
                needsConfiguration: true
            };
        }

        return { success: false, error: error.message };
    }
};

/**
 * Capture PayPal payment after customer approval
 * Called after customer approves on PayPal
 * 
 * @param {string} paypalOrderId - PayPal order ID from createOrder
 * @param {string} orderId - Firestore order ID
 * @returns {Promise<{success: boolean, orderId?: string, error?: string}>}
 */
export const capturePayPalPayment = async (paypalOrderId, orderId) => {
    try {
        const captureOrder = httpsCallable(functions, 'capturePayPalOrder');

        const result = await captureOrder({
            paypalOrderId,
            orderId
        });

        if (result.data?.success) {
            return {
                success: true,
                orderId: result.data.orderId,
                captureId: result.data.captureId,
                status: result.data.status
            };
        } else {
            throw new Error(result.data?.error || 'Failed to capture payment');
        }
    } catch (error) {
        console.error('Error capturing PayPal payment:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Create PayPal order for use with PayPal Buttons component
 * This is a wrapper that returns just the PayPal order ID
 * 
 * @param {Array} items - Cart items
 * @param {string} customerEmail - Customer email
 * @param {string} customerName - Customer name
 * @returns {Promise<string>} PayPal Order ID
 */
export const createPayPalOrderForButtons = async (items, customerEmail, customerName) => {
    const result = await createPayPalOrder(items, customerEmail, customerName);
    if (result.success && result.paypalOrderId) {
        // Store the Firestore order ID for later
        sessionStorage.setItem('pendingOrderId', result.orderId);
        return result.paypalOrderId;
    }
    throw new Error(result.error || 'Failed to create order');
};

/**
 * Handle PayPal approval for use with PayPal Buttons component
 * 
 * @param {Object} data - PayPal approval data
 * @param {Object} actions - PayPal actions object (not used, we capture server-side)
 * @returns {Promise<{success: boolean, orderId?: string, error?: string}>}
 */
export const onPayPalApprove = async (data, actions) => {
    // Get the Firestore order ID we stored earlier
    const orderId = sessionStorage.getItem('pendingOrderId');

    // Capture the payment server-side
    const result = await capturePayPalPayment(data.orderID, orderId);

    // Clear the pending order
    sessionStorage.removeItem('pendingOrderId');

    return result;
};
